import type { TmsEvent, TrailerExchange } from "@storyboard/web-client";
import { isCallRejection } from "@/components/feed/tmsEvent/tmsEventDisplay";

/** What a rejection's popover shows: the TMS's status above the driver's call. */
export type CallRejectionDetail = {
  statusCode: string;
  statusDesc: string;
  statusMsg: string;
  callLines: readonly string[];
};

/** The popover's content for an event, or null when it has none — every v1 rejection and every other kind. */
export function callRejectionDetailOf(event: TmsEvent): CallRejectionDetail | null {
  if (!isCallRejection(event) || event.version !== 2) return null;
  const { statusCode, statusDesc, statusMsg, callLines } = event;
  return { statusCode, statusDesc, statusMsg, callLines };
}

/** What a version 2 call's popover shows. */
export type CallTrailerDetail = {
  kind: "emptyCall" | "loadedCall";
  stopName: string;
  city: string | null;
  trailerExchange: TrailerExchange | null;
  droppedTrailerNumber: string | null;
  departingTrailerNumber: string | null;
};

/** The popover's content for a version 2 call, or null for a version 1 call and every other kind. */
export function callTrailerDetailOf(event: TmsEvent): CallTrailerDetail | null {
  if ((event.kind !== "emptyCall" && event.kind !== "loadedCall") || event.version !== 2) return null;
  const { kind, stopName, city, trailerExchange, droppedTrailerNumber, departingTrailerNumber } = event;
  return { kind, stopName, city, trailerExchange, droppedTrailerNumber, departingTrailerNumber };
}

/** Everything a chip can open. */
export type TmsEventDetail =
  | { type: "callRejection"; detail: CallRejectionDetail }
  | { type: "callTrailer"; detail: CallTrailerDetail };

/** What a chip opens on click, or null when it opens nothing. */
export function tmsEventDetailOf(event: TmsEvent): TmsEventDetail | null {
  const rejection = callRejectionDetailOf(event);
  if (rejection) return { type: "callRejection", detail: rejection };
  const trailer = callTrailerDetailOf(event);
  return trailer ? { type: "callTrailer", detail: trailer } : null;
}
